import React, { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ReactComponent as PortfolioIcon } from '$/assets/icons/Portfolio.svg';
import { ReactComponent as NEIcon } from '$/assets/icons/NewsEarnings.svg';
import { ReactComponent as LogoutIcon } from '$/assets/icons/Logout.svg';
import { ReactComponent as ClientIcon } from '$/assets/icons/client.svg';
import { ReactComponent as CompanyIcon } from '$/assets/icons/company.svg';
import { ReactComponent as UserIcon } from '$/assets/icons/users.svg';
import { ReactComponent as DataIcon } from '$/assets/icons/data.svg';
import { ReactComponent as SettingsIcon } from '$/assets/icons/settings.svg';
import { ReactComponent as DocumentsIcon } from '$/assets/icons/Documents.svg';
import { ReactComponent as MagnifyIcon } from '$/assets/icons/MagnifyingGlass.svg';
import { ReactComponent as MagnifyGraphIcon } from '$/assets/icons/magnifying-glass-chart-solid.svg';
import { ReactComponent as DocumentWithTextIcon } from '$/assets/icons/DocumentWithText.svg';
import { ReactComponent as Padlock } from '$/assets/icons/padlock.svg';
import { ReactComponent as ScalesOfJusticeIcon } from '$/assets/icons/ScalesOfJustice.svg';
import './index.scss';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import {
  getBixbyComplianceFeatureFlagState,
  getClosFeatureFlagState,
  getCompanyHistoryFeatureFlagState,
  getCreditSightsCovenantReviewFeatureFlagState,
} from '$/store/release-feature-flags/selectors';
import {
  getIsCurrentUserBixbyComplianceAdminState,
  getIsCurrentUserClientComplianceAdminState,
  getIsCurrentUserClientConsultantState,
} from '$/store/profile/selectors';

interface ISideNavProps {
  onAdminPortal?: boolean;
  contactUsLink?: string;
  featureFlags: string[];
  currentAdminCompany: Bixby.Models.AdminCompany | undefined;
  isClosEnabled: boolean;
  isCompanyHistoryEnabled: boolean;
  isClientComplianceAdmin: boolean;
  isCreditSightsCovenantReviewEnabled: boolean;
  isBixbyComplianceAdmin: boolean;
  isClientConsultant: boolean;
}

const SideNav: React.FC<ISideNavProps> = props => {
  const {
    onAdminPortal,
    contactUsLink,
    featureFlags,
    currentAdminCompany,
    isClosEnabled,
    isCompanyHistoryEnabled,
    isClientComplianceAdmin,
    isCreditSightsCovenantReviewEnabled,
    isBixbyComplianceAdmin,
    isClientConsultant,
  } = props;
  const smallItems = (
    <div className="small_items">
      <Link to="/terms-of-service" className="small_item">
        Terms of Service
      </Link>
      <a href={Bixby.Configuration.privacyPolicyUrl} className="small_item">
        Privacy Policy
      </a>
      <Link to="/disclosures" className="small_item">
        Disclosures
      </Link>
      {contactUsLink && (
        <a href={contactUsLink} className="small_item">
          Contact Us
        </a>
      )}
    </div>
  );

  const { pathname } = useLocation();

  const isPrivateCompanyActive = useMemo(() => {
    const rootUrl = '/admin/company';
    if (location.pathname === rootUrl) {
      return true;
    } else if (
      location.pathname.includes(rootUrl) &&
      currentAdminCompany &&
      !Bixby.Utils.isNullOrEmpty(currentAdminCompany) &&
      !currentAdminCompany.isSECPublic
    ) {
      return true;
    } else {
      return false;
    }
  }, [currentAdminCompany]);

  const isPublicCompanyActive = useMemo(() => {
    const rootPublicUrl = '/admin/public-company';
    const rootPrivateUrl = '/admin/company';
    if (location.pathname === rootPublicUrl) {
      return true;
    } else if (
      location.pathname.includes(rootPrivateUrl) &&
      currentAdminCompany &&
      !Bixby.Utils.isNullOrEmpty(currentAdminCompany) &&
      currentAdminCompany.isSECPublic
    ) {
      return true;
    } else {
      return false;
    }
  }, [currentAdminCompany]);

  return onAdminPortal ? (
    <div className="sideNav">
      <div className="main_items">
        <div className="nav_item category">Manage Groups</div>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/client">
          <div className="icon">
            <ClientIcon />
          </div>
          Clients
        </NavLink>
        <NavLink
          className={({ isActive }) => {
            return 'nav_item' + ((isActive || isPrivateCompanyActive) && !isPublicCompanyActive ? ' active' : '');
          }}
          to="/admin/company">
          <div className="icon">
            <CompanyIcon />
          </div>
          Companies
        </NavLink>
        {featureFlags.includes(Bixby.Constants.CLIENT_COMPANY_TRANSCRIPTIONS_VIEW) && (
          <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/transcripts">
            <div className="icon">
              <DataIcon />
            </div>
            Transcripts
          </NavLink>
        )}
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/pending-approval">
          <div className="icon">
            <DataIcon />
          </div>
          Docs Pending Approval
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/user">
          <div className="icon">
            <UserIcon />
          </div>
          Users
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/data-sites">
          <div className="icon">
            <DataIcon />
          </div>
          Data Sites
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/reconciliations">
          <div className="icon">
            <DataIcon />
          </div>
          Reconciliations
        </NavLink>
        <NavLink
          className={({ isActive }) =>
            'nav_item' + ((isActive || isPublicCompanyActive) && !isPrivateCompanyActive ? ' active' : '')
          }
          to="/admin/public-company">
          <div className="icon">
            <CompanyIcon />
          </div>
          Public Companies
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/clo/manage">
          <div className="icon">
            <MagnifyGraphIcon />
          </div>
          CLOs
        </NavLink>
        {isBixbyComplianceAdmin && (
          <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/compliance">
            <div className="icon">
              <Padlock />
            </div>
            Compliance
          </NavLink>
        )}
        <NavLink
          className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')}
          to="/admin/private-equity-sponsors">
          <div className="icon">
            <UserIcon />
          </div>
          Private Equity Sponsors
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/admin/settings">
          <div className="icon">
            <SettingsIcon />
          </div>
          Settings
        </NavLink>
      </div>
      {smallItems}
    </div>
  ) : (
    <div className="sideNav">
      <div className="main_items">
        <NavLink to="/portfolio" className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')}>
          <div className="icon">
            <PortfolioIcon />
          </div>
          Portfolio
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/news-earnings">
          <div className="icon">
            <NEIcon />
          </div>
          News &amp; Earnings
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/transaction-comps">
          <div className="icon">
            <DocumentsIcon />
          </div>
          Transaction Comps
        </NavLink>
        <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/data-sites">
          <div className="icon">
            <DataIcon />
          </div>
          Data Sites
        </NavLink>
        {featureFlags.includes(Bixby.Constants.CLIENT_ANALYTICS_VIEW) && (
          <NavLink className={({ isActive }) => 'nav_item' + (isActive ? ' active' : '')} to="/analytics">
            <div className="icon">
              <MagnifyIcon />
            </div>
            Analytics
          </NavLink>
        )}
        {featureFlags.includes(Bixby.Constants.CLIENT_CLO_VIEW) && isClosEnabled && (
          <NavLink
            className={({ isActive }) =>
              'nav_item' + (isActive || pathname.indexOf('/clodeal-analytics') != -1 ? ' active' : '')
            }
            to="/clo-analytics">
            <div className="icon">
              <MagnifyGraphIcon />
            </div>
            CLOs
          </NavLink>
        )}
        {featureFlags.includes(Bixby.Constants.CLIENT_COMPANY_DOCUMENTS) &&
          isCompanyHistoryEnabled &&
          !isClientConsultant && (
            <NavLink
              className={({ isActive }) => 'nav_item company_documents' + (isActive ? ' active' : '')}
              to="/company-documents">
              <div className="icon">
                <DocumentWithTextIcon />
              </div>
              Documents
            </NavLink>
          )}
        {featureFlags.includes(Bixby.Constants.CLIENT_BIXBY_COMPLIANCE) && isClientComplianceAdmin && (
          <NavLink className={({ isActive }) => 'nav_item compliance' + (isActive ? ' active' : '')} to="/compliance">
            <div className="icon">
              <Padlock />
            </div>
            Compliance
          </NavLink>
        )}
        {featureFlags.includes(Bixby.Constants.CLIENT_CREDITSIGHTS_COVENANT_REVIEW) &&
          isCreditSightsCovenantReviewEnabled && (
            <NavLink
              className={({ isActive }) => 'nav_item covenant_review' + (isActive ? ' active' : '')}
              to="/covenant-review">
              <div className="icon">
                <ScalesOfJusticeIcon />
              </div>
              Covenant Review
            </NavLink>
          )}
        <NavLink className={({ isActive }) => 'nav_item side_logout' + (isActive ? ' active' : '')} to="/logout">
          <div className="icon">
            <LogoutIcon />
          </div>
          Logout
        </NavLink>
      </div>
      {smallItems}
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isClientComplianceAdmin } = getIsCurrentUserClientComplianceAdminState(state);
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isClosEnabled } = getClosFeatureFlagState(state);
  const { isCompanyHistoryEnabled } = getCompanyHistoryFeatureFlagState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);
  const { isCreditSightsCovenantReviewEnabled } = getCreditSightsCovenantReviewFeatureFlagState(state);
  const { isClientConsultant } = getIsCurrentUserClientConsultantState(state);

  return {
    currentAdminCompany: state.companyState.currentCompany,
    isClosEnabled,
    isCompanyHistoryEnabled,
    isClientComplianceAdmin: isClientComplianceAdmin && isBixbyComplianceEnabled,
    isCreditSightsCovenantReviewEnabled,
    isBixbyComplianceAdmin: isBixbyComplianceAdmin && isBixbyComplianceEnabled,
    isClientConsultant: isClientConsultant,
  };
};

export default connect(mapStateToProps)(SideNav);
